import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { DBTable } from '../components/DBTable';
import { useDataTable } from '../hooks/useDataTable';
import { usePermission } from '../hooks/usePermission';
import { AccessLevel } from '../types';
import { X, Save, AlertCircle, Upload, ShieldCheck, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

export const ConfigurationPage: React.FC = () => {
  const { hasPermission } = usePermission();
  
  const hasAccess = hasPermission('app.sistema.configurazione', AccessLevel.ro);

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center bg-white rounded-3xl border border-slate-100 shadow-xs max-w-2xl mx-auto my-12">
        <div id="no-access-config-icon-wrapper" className="w-16 h-16 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mb-4 border border-red-100/50">
          <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 15v2m0-6v4m0-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h1 className="text-xl font-black text-slate-800 uppercase tracking-tight">Accesso non consentito</h1>
        <p className="text-sm text-slate-500 mt-2 max-w-sm font-semibold">
          Don't have permission to access. È richiesto l'inserimento nel gruppo <code className="bg-slate-50 border border-slate-100 px-1.5 py-0.5 rounded-lg font-mono text-xs text-red-600 font-bold">app.sistema.configurazione</code>.
        </p>
      </div>
    );
  }

  const tableName = 'cpgev_config';
  const { insertRow, updateRow } = useDataTable({ tableName });
  
  const [editingRow, setEditingRow] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ id: '', data: '' });
  const [jsonError, setJsonError] = useState<string | null>(null);

  const handleEdit = (row: any) => {
    setEditingRow(row);
    setFormData({
      id: row.id,
      data: JSON.stringify(row.data, null, 2)
    });
    setJsonError(null);
    setIsModalOpen(true);
  };

  const handleAdd = () => {
    setEditingRow(null);
    setFormData({ id: '', data: '{\n}' });
    setJsonError(null);
    setIsModalOpen(true);
  };

  const validateJson = (str: string) => {
    try {
      JSON.parse(str);
      return true;
    } catch (e: any) {
      setJsonError(e.message);
      return false;
    }
  };

  const handleSave = async () => {
    if (!formData.id.trim()) {
      toast.error('ID è obbligatorio');
      return;
    }

    if (!validateJson(formData.data)) {
      toast.error('JSON non valido');
      return;
    }

    try {
      const payload = {
        id: formData.id,
        data: JSON.parse(formData.data),
        updated_at: new Date().toISOString()
      };

      if (editingRow) {
        await updateRow(editingRow.id, payload);
      } else {
        await insertRow(payload);
      }
      setIsModalOpen(false);
    } catch (err) {
      // Error handled by useDataTable
    }
  };

  return (
    <div className="pb-6 space-y-6 pt-0">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-bold text-slate-900">Configurazione Sistema</h1>
          <p className="text-slate-500">Gestione dei parametri di configurazione dell'applicazione (Tabella cpgev_config)</p>
        </div>
        <Link
          to="/sistema/import-comuni"
          className="self-start sm:self-auto px-5 py-2.5 bg-white hover:bg-slate-50 text-slate-700 hover:text-slate-900 text-sm font-semibold rounded-xl border border-slate-200 hover:border-slate-300 flex items-center gap-2 transition-all shadow-sm"
        >
          <Upload className="w-4 h-4 text-slate-500" />
          Importa Comuni e CAP
        </Link>
      </div>

      {/* Guida e Sicurezza Chiavi API */}
      <div className="bg-gradient-to-r from-emerald-50/75 to-teal-50/40 border border-emerald-100/60 rounded-2xl p-5 space-y-4 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="p-2.5 bg-emerald-500/10 border border-emerald-500/15 rounded-xl text-emerald-600 shrink-0">
            <ShieldCheck className="w-5 h-5 animate-pulse" />
          </div>
          <div className="space-y-1">
            <h3 className="font-bold text-slate-800 text-sm flex items-center gap-2">
              Sicurezza & Limitazione API Key (Google Maps Platform)
            </h3>
            <p className="text-slate-600 text-xs leading-relaxed">
              La chiave <code>GOOGLE_MAPS_PLATFORM_KEY</code> viene scaricata ed eseguita sul client (browser) per visualizzare la mappa interattiva e gestire la geocodifica degli indirizzi nei profili del personale GEV. Essendo visibile pubblicamente nei pacchetti di rete, <strong>è di fondamentale importanza limitarla</strong> per prevenire addebiti imprevisti da utilizzi non autorizzati.
            </p>
          </div>
        </div>

        <div className="bg-white/90 border border-slate-100 rounded-xl p-4 text-xs text-slate-600 space-y-3">
          <p className="font-semibold text-slate-700">📌 Come applicare le restrizioni di sicurezza sulla tua chiave in pochi passi:</p>
          <ol className="list-decimal pl-4 space-y-2.5 text-[11px] text-slate-600">
            <li>
              Apri le <a href="https://console.cloud.google.com/google/maps-apis/credentials?project=gesvol-351d5" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-0.5 font-bold">Credenziali API di Google Cloud Console <ExternalLink className="w-3.5 h-3.5 inline" /></a>
            </li>
            <li>
              Trova la chiave API configurata per le mappe o creane una nuova (es. "Gesvol Maps Frontend Key") e imposta le seguenti limitazioni:
              <ul className="list-disc pl-4 mt-2 space-y-1 text-slate-500 font-medium">
                <li>
                  <strong className="text-slate-750">Restrizioni dei datori di chiamata (Referrer HTTP):</strong> Seleziona <em>Referrer HTTP (siti web)</em> ed aggiungi i tuoi domini di produzione e staging per consentire l'esecuzione solo lì:
                  <div className="mt-1 flex flex-wrap gap-1.5 font-mono text-[10px]">
                    <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded border border-slate-200">*.gevbologna.org/*</span>
                    <span className="bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded border border-slate-200">*.run.app/*</span>
                  </div>
                </li>
                <li className="pt-1">
                  <strong className="text-slate-750">Restrizioni API:</strong> Sotto <em>Restrizioni API</em> seleziona l'opzione "Limita chiave" e spunta solo le due API abilitate necessarie alla geocodifica ed al disegno:
                  <div className="mt-1 flex flex-wrap gap-1.5 font-mono text-[10px]">
                    <span className="bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded border border-teal-100 font-semibold">Maps JavaScript API</span>
                    <span className="bg-teal-50 text-teal-700 px-1.5 py-0.5 rounded border border-teal-100 font-semibold">Geocoding API</span>
                  </div>
                </li>
              </ul>
            </li>
          </ol>
        </div>
      </div>

      <DBTable 
        tableName={tableName}
        title="Parametri di Configurazione"
        onEdit={handleEdit}
        onAdd={handleAdd}
        sortable={true}
        searchable={true}
        filterable={true}
        selectable={true}
      />

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
            <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <h3 className="text-lg font-semibold text-slate-800">
                {editingRow ? 'Modifica Configurazione' : 'Nuova Configurazione'}
              </h3>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-2 hover:bg-slate-200 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-slate-700">ID Configurazione</label>
                <input
                  type="text"
                  value={formData.id}
                  onChange={(e) => setFormData({ ...formData, id: e.target.value })}
                  disabled={!!editingRow}
                  placeholder="es. menu, settings, theme..."
                  className="w-full px-4 py-2 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-slate-50 disabled:text-slate-500"
                />
              </div>

              <div className="space-y-1.5 flex-1 flex flex-col">
                <label className="text-sm font-medium text-slate-700">Dati (JSON)</label>
                <textarea
                  value={formData.data}
                  onChange={(e) => {
                    setFormData({ ...formData, data: e.target.value });
                    if (jsonError) validateJson(e.target.value);
                  }}
                  placeholder='{ "key": "value" }'
                  className={cn(
                    "w-full flex-1 min-h-[300px] px-4 py-3 font-mono text-sm border rounded-xl focus:outline-none focus:ring-2 transition-all resize-none",
                    jsonError ? "border-destructive focus:ring-destructive/20" : "border-slate-200 focus:ring-primary/20 focus:border-primary"
                  )}
                />
                {jsonError && (
                  <div className="flex items-center gap-1.5 text-xs text-destructive mt-1">
                    <AlertCircle className="w-3 h-3" />
                    <span>JSON non valido: {jsonError}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="p-6 border-t border-slate-100 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-6 py-2 text-sm font-medium text-slate-600 hover:bg-slate-200 rounded-xl transition-colors"
              >
                Annulla
              </button>
              <button
                onClick={handleSave}
                className="px-6 py-2 text-sm font-medium bg-primary text-white rounded-xl hover:bg-primary/90 transition-colors flex items-center gap-2"
              >
                <Save className="w-4 h-4" />
                Salva
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
